<script setup lang="ts">
import { useNuxtApp } from '#app'
import { ref } from 'vue'
import HeaderPage   from '~/pages/header.vue'
import MenuPage     from '~/pages/menu.vue'
import CarouselPage from '~/pages/carousel.vue'
import CartPage     from '~/pages/cart.vue'
import { useVmStore } from '@/stores/vm'
import ShoppingPanel from '~/components/ShoppingList.vue'
import { ranaSignInMarket } from '@monorepo/ui'
import IconCanvas from '~/assets/icons/IconCanvas.vue'
import IconDiscover from '~/assets/icons/IconDiscover.vue'
import IconCart from '~/assets/icons/IconCart.vue'
import { useShoppingLists } from '@/composables/shoppingLists'  // <<— import the composable
import {loadInitialAuthData} from "../utils/jwtToken.js"

// grab your VM store as before
const vm = useVmStore()

// fire off the shopping-lists composable (runs its onMounted hydrate logic)
useShoppingLists()

// your existing layout logic…
const activeTab = ref<'menu'|'main'|'cart'>('main')
const setActiveTab = (tab: typeof activeTab.value) => activeTab.value = tab
const isTabletView = ref(false)
const checkView = () => { isTabletView.value = window.innerWidth >= 768 && window.innerWidth <= 1024 }

const openShoppingPanel = () => {
  const { $vm } = useNuxtApp()
  $vm.openRightPanel(ShoppingPanel)
}
const openLoginPanel = () => {
  const { $vm } = useNuxtApp()
  if (window.innerWidth < 768) {
    setActiveTab('cart')
    // Small delay to ensure tab switch animation completes
    setTimeout(() => {
      $vm.openRightPanel(ranaSignInMarket)
    }, 100)
  } else {
    $vm.openRightPanel(ranaSignInMarket)
  }
}
const handlePanelClose = () => {

  vm.closeRightPanel()
   if (window.innerWidth < 768) {
    setActiveTab('main')
  }
}

const handlePanelCloseWithLogin = () => {
  const token = localStorage.getItem('auth_token') || ''
  if (token.length > 10) vm.user.jwtToken = token

    const { token: initialJwtToken, expiry: initialJwtTokenExpiry, countryCode: initialJwtCountryCode, email: initialUserEmail } = loadInitialAuthData()
    vm.user.email=initialUserEmail
    vm.user.jwtToken= initialJwtToken
    vm.user.loginExpires= initialJwtTokenExpiry
    vm.user.countryCode= initialJwtCountryCode 
  
  vm.closeRightPanel()
    // On mobile devices, switch to main tab after login
  if (window.innerWidth < 768) {
    setActiveTab('main')
  }
}

const showMenu = computed(() => {
  if (isTabletView.value) return !vm.isRightPanelOpen
  return true
})

const setupResizeListener = () => {
  checkView()
  window.addEventListener('resize', checkView)
}

onMounted(() => {
  setupResizeListener()
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', checkView)
})
watch(activeTab, (newTab, oldTab) => {
  // When switching away from cart tab, reset panel to default cart view
  if (oldTab === 'cart' && newTab !== 'cart') {
    // Only do this if we're not in the sign-in process
    if (vm.rightPanelComponent && vm.rightPanelComponent.name !== 'ranaSignInMarket') {
      vm.closeRightPanel()
    }
  }
  
  // When switching to cart tab, ensure cart is visible
  if (newTab === 'cart') {
    // If sign-in panel isn't open, ensure shopping panel is open
    if (!vm.isRightPanelOpen || 
        (vm.rightPanelComponent && vm.rightPanelComponent.name !== 'ranaSignInMarket')) {
      openShoppingPanel()
    }
  }
})
</script>


<template>
  <div class="flex flex-col h-screen">
    <!-- fixed header -->
    <HeaderPage @open-login-panel="openLoginPanel" />

    <!-- Desktop Layout -->
    <div class="hidden md:flex flex-1" v-if="!vm.isMobile">
      <!-- left menu -->
      <MenuPage v-if="showMenu"/>

      <!-- main content -->
      <div class="flex flex-col flex-1 overflow-hidden">
        <div
          v-if="vm.showCarousel"
          class="w-full px-4"
          :style="{ height: vm.carouselHeight }"
        >
          <CarouselPage :slides="vm.carouselProducts" display-mode="landscape"/>
        </div>
        <main
          class="flex-1 overflow-y-auto highlight-scrollbar
                 bg-[hsl(var(--background))] text-[hsl(var(--content))]"
        >
          <slot />
        </main>
      </div>

      <!-- right column: panel + cart -->
      <div
        class="flex flex-col overflow-hidden transition-all duration-300 h-[calc(100vh-184px)]"
         :class="vm.isRightPanelOpen ? 'w-72 pt-2 pb-2' : 'w-0 p-0'"
      >
        <!-- dynamic slide-in panel -->
        <section
          v-show="vm.isRightPanelOpen && vm.rightPanelComponent"
          class="flex-1 overflow-y-auto bg-neutral rounded-lg shadow-lg p-2"
        >
          <component class="mt-5 lg:mt-0" :is="vm.rightPanelComponent"  @close="handlePanelClose" @closeLogin="handlePanelCloseWithLogin"/>
        </section>

        <!-- cart at bottom -->
        <div class="mt-auto mr-0">
          <CartPage @open-shopping-panel="openShoppingPanel" />
        </div>
      </div>
    </div>

    <!-- Mobile Layout with Tabs -->
    <div class="flex flex-col md:hidden flex-1">
      <!-- Tab Content -->
      <div class="flex-1 overflow-hidden">
        <!-- Menu Tab Content -->
        <div v-show="activeTab === 'menu'" class="h-full overflow-y-auto">
          <MenuPage />
        </div>

        <!-- Main Content Tab -->
        <div v-show="activeTab === 'main'" class="h-full flex flex-col">
          <div
            v-if="vm.showCarousel"
            class="w-full px-4"
            :style="{ height: vm.carouselHeight }"
          >
            <CarouselPage :slides="vm.carouselProducts" display-mode="landscape"/>
          </div>
          <main
            class="flex-1 overflow-y-auto highlight-scrollbar
                  bg-[hsl(var(--background))] text-[hsl(var(--content))]"
          >
            <slot />
          </main>
        </div>

        
      <!-- right column: panel + cart -->
     <div v-show="activeTab === 'cart'" class="sm:w-full h-full overflow-y-auto">
          <div v-if="vm.isRightPanelOpen && vm.rightPanelComponent" class="mb-4">
            <component 
            :is="vm.rightPanelComponent"   @close="handlePanelClose" 
            @closeLogin="handlePanelCloseWithLogin"/>
          </div>
          <CartPage 
          v-if="!vm.isRightPanelOpen || 
                    (vm.rightPanelComponent && vm.rightPanelComponent.name !== 'ranaSignInMarket')"
              @open-shopping-panel="openShoppingPanel" />
        </div>
      </div>

      <!-- Bottom Tab Navigation -->
      <div class="bg-neutral h-16 -mt-15 z-50 flex items-center justify-around border-t border-[#00ccaa]/30 shadow-lg">
        <!-- Menu Tab -->
        <button 
        class="flex flex-col items-center justify-center w-1/3 h-full"
        :class="activeTab === 'menu' ? 'text-[#64F4D5] border-t-2 border-[#64F4D5]' : 'text-[#00ccaa]'"
        @click="setActiveTab('menu')"
        >
          <IconDiscover class="h-8 w-8"/>
          <span class="text-xs mt-1">Menu</span>
        </button>

        <!-- Main Content Tab -->
        <button 
          @click="setActiveTab('main')"
          class="flex flex-col items-center justify-center w-1/3 h-full"
          :class="activeTab === 'main' ? 'text-[#64F4D5] border-t-2 border-[#64F4D5]' : 'text-[#00ccaa]'"
        >
          <IconCanvas class="h-8 w-8"/>
          <span class="text-xs mt-1">Home</span>
        </button>

        <!-- Cart Tab -->
            
       <button 
       class="flex flex-col items-center justify-center w-1/3 h-full"
       :class="activeTab === 'cart' ? 'text-[#64F4D5] border-t-2 border-[#64F4D5]' : 'text-[#00ccaa]'"
       @click="setActiveTab('cart')"
  >
    <IconCart class="h-9 w-9"/>
    <span class="text-xs mt-1">Cart</span>
  </button>
  
      </div>
    </div>
  </div>
</template>